import { useEffect, useState } from 'react';
import BrandLogo from '@/components/base/BrandLogo';
import ImageUpload from '@/components/base/ImageUpload';
import { fetchSiteSettings, updateSiteSettings } from '@/lib/api';
import type { LogoSettings } from '@/lib/types';

const DEFAULT_CFG: LogoSettings = {
  size: 40,
  background: '',
  padding: 0,
  shape: 'circle',
  useBackground: false,
};

export default function LogoManager() {
  const [logoUrl, setLogoUrl] = useState('');
  const [churchName, setChurchName] = useState('');
  const [cfg, setCfg] = useState<LogoSettings>(DEFAULT_CFG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchSiteSettings()
      .then((s) => {
        setLogoUrl(s.logo_url ?? '');
        setChurchName(s.church_name ?? '');
        setCfg({ ...DEFAULT_CFG, ...(s.logo_settings ?? {}) });
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load logo settings'))
      .finally(() => setLoading(false));
  }, []);

  const set = <K extends keyof LogoSettings>(key: K, value: LogoSettings[K]) =>
    setCfg((p) => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      await updateSiteSettings({ logo_url: logoUrl, logo_settings: cfg });
      setMessage('Logo saved. It will update across the website header and footer.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save logo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="bg-white rounded-lg border border-background-200 p-6">
        <p className="text-center text-foreground-400 py-6">
          <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading logo...
        </p>
      </section>
    );
  }

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';

  return (
    <section className="bg-white rounded-lg border border-background-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-image-circle-line text-primary-600" aria-hidden="true" />
        <h3 className="font-heading font-semibold text-base text-foreground-900">Logo &amp; Branding</h3>
      </div>
      <p className="text-sm text-foreground-500 mb-5">
        Upload your church logo and fine-tune its size, shape, background and padding. The preview matches how it appears
        in the website header and footer.
      </p>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">{message}</div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">{error}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Controls */}
        <div className="space-y-5">
          <div>
            <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Logo Image</label>
            <ImageUpload value={logoUrl} onChange={setLogoUrl} folder="branding" />
          </div>

          <div>
            <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
              Size: {cfg.size ?? 40}px
            </label>
            <input
              type="range"
              min={24}
              max={96}
              value={cfg.size ?? 40}
              onChange={(e) => set('size', Number(e.target.value))}
              className="w-full accent-primary-500"
            />
          </div>

          <div>
            <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Shape</label>
            <div className="flex gap-2">
              {(['circle', 'rounded', 'square'] as const).map((shape) => (
                <button
                  key={shape}
                  type="button"
                  onClick={() => set('shape', shape)}
                  className={`px-3 py-1.5 rounded-md text-xs font-label capitalize border transition-colors cursor-pointer ${
                    (cfg.shape ?? 'circle') === shape
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'border-background-300 text-foreground-600 hover:border-primary-400'
                  }`}
                >
                  {shape}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cfg.useBackground ?? false}
              onChange={(e) => set('useBackground', e.target.checked)}
              className="w-4 h-4 accent-primary-500"
            />
            <span className="text-sm text-foreground-700">Add a background behind the logo</span>
          </label>

          {cfg.useBackground && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Background Colour</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={cfg.background || '#ffffff'}
                    onChange={(e) => set('background', e.target.value)}
                    className="w-10 h-10 rounded-md border border-background-300 cursor-pointer bg-white"
                    aria-label="Background colour"
                  />
                  <input
                    type="text"
                    value={cfg.background || ''}
                    onChange={(e) => set('background', e.target.value)}
                    placeholder="#ffffff"
                    className={inputClass}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                  Padding: {cfg.padding ?? 0}px
                </label>
                <input
                  type="range"
                  min={0}
                  max={20}
                  value={cfg.padding ?? 0}
                  onChange={(e) => set('padding', Number(e.target.value))}
                  className="w-full accent-primary-500 mt-3"
                />
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div>
          <label className="block text-xs font-label font-medium text-foreground-700 mb-1.5">Preview</label>
          <div className="rounded-lg border border-background-200 overflow-hidden">
            <div className="bg-white flex items-center justify-center py-10 px-6">
              <BrandLogo url={logoUrl} name={churchName || 'TGMCI'} settings={cfg} />
            </div>
            <div className="bg-foreground-950 flex items-center justify-center py-8 px-6 border-t border-background-200">
              <BrandLogo url={logoUrl} name={churchName || 'TGMCI'} settings={cfg} />
            </div>
          </div>
          {!logoUrl && (
            <p className="text-xs text-foreground-400 mt-2">
              No logo uploaded yet — a clean monogram is shown as a placeholder.
            </p>
          )}
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <button type="button" onClick={save} disabled={saving} className="btn-primary whitespace-nowrap cursor-pointer">
          {saving ? 'Saving...' : 'Save Logo'}
        </button>
      </div>
    </section>
  );
}